import React from 'react'
import { Button, Grid, Typography, Chip, Tooltip } from '@material-ui/core'
import GetAppIcon from '@material-ui/icons/GetApp'
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile'
import styled from 'styled-components'
// @ts-ignore
import { CustomModal } from '../../../../components/common/CustomModal'
import { ImportCollectionStatus, MigrationCollections } from '../../../../types/migration'
import {
  formatImportTimestamp,
  getImportStatusPresentation,
  hasImportDownload,
  parseImportError,
} from './importStatusUtils'

const StatusChip = styled(Chip)<{ statuscolor: string }>`
  && {
    background-color: ${(props) => props.statuscolor};
    color: white;
    font-weight: 500;
  }
`

const StatusContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`

const FileNameContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 12px;
  background-color: #f5f5f5;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  min-width: 0;
`

const FileNameText = styled(Typography)`
  && {
    font-weight: 500;
    flex: 1;
    min-width: 0;
    word-break: break-all;
    overflow-wrap: break-word;
  }
`

const DownloadButton = styled(Button)`
  && {
    text-transform: none;
    color: white;
    padding: 10px 20px;
    font-weight: 500;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

    &:hover {
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
    }
  }
`

const SectionTitle = styled(Typography)`
  && {
    font-weight: 600;
    font-size: 14px;
    color: #424242;
    margin-bottom: 8px;
  }
`

interface ImportStatusDetailsModalProps {
  open: boolean
  onClose: () => void
  collectionName: MigrationCollections | null
  importStatus?: ImportCollectionStatus
}

export const ImportStatusDetailsModal: React.FC<ImportStatusDetailsModalProps> = ({
  open,
  onClose,
  collectionName,
  importStatus,
}) => {
  if (!collectionName) {
    return null
  }

  const presentation = getImportStatusPresentation(importStatus?.status)
  const updatedAt = formatImportTimestamp(importStatus?.updatedAt)
  const errorInfo = parseImportError(importStatus?.error)
  const hasDetails = errorInfo.details.length > 0
  const showDownload = hasImportDownload(importStatus)
  const fileName = importStatus?.fileName ? importStatus.fileName.split('/').pop() : null
  let formattedRawCause: string | null = null

  if (errorInfo.rawCause) {
    const parsed = (() => {
      try {
        return JSON.parse(errorInfo.rawCause as string)
      } catch (_error) {
        return null
      }
    })()

    formattedRawCause = parsed ? JSON.stringify(parsed, null, 2) : (errorInfo.rawCause as string)
  }

  return (
    <CustomModal open={open} onClose={onClose} size="medium">
      <CustomModal.Header title={`${collectionName} import details`} />
      <CustomModal.Content>
        {importStatus ? (
          <Grid container direction="column" spacing={16}>
            <Grid item>
              <SectionTitle>Status</SectionTitle>
              <StatusContainer>
                <StatusChip label={presentation.label} statuscolor={presentation.background} />
                {updatedAt && (
                  <Typography variant="body2" style={{ color: '#616161' }}>
                    Imported at {updatedAt}
                  </Typography>
                )}
              </StatusContainer>
            </Grid>
            {(errorInfo.message || hasDetails || formattedRawCause) && (
              <Grid item style={{ width: '100%' }}>
                <SectionTitle>Errors</SectionTitle>
                {errorInfo.message && (
                  <Typography color="error" style={{ marginBottom: 8 }}>
                    {errorInfo.message}
                  </Typography>
                )}
                {hasDetails && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {errorInfo.details.map((detail, index) => (
                      <div key={`${detail.property || detail.header || 'detail'}-${index}`}>
                        <Typography style={{ fontWeight: 500 }}>
                          {detail.value ||
                            detail.property ||
                            detail.header ||
                            `Row ${detail.index}`}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {detail.message || 'No message provided'}
                        </Typography>
                      </div>
                    ))}
                  </div>
                )}
                {formattedRawCause && (
                  <div style={{ marginTop: 12 }}>
                    <Typography style={{ fontWeight: 500, marginBottom: 4 }}>
                      Raw error payload
                    </Typography>
                    <div
                      style={{
                        backgroundColor: '#f5f5f5',
                        borderRadius: 4,
                        maxHeight: 240,
                        overflow: 'auto',
                        padding: 12,
                      }}
                    >
                      <pre
                        style={{
                          margin: 0,
                          fontSize: 12,
                          lineHeight: 1.6,
                          whiteSpace: 'pre',
                        }}
                      >
                        {formattedRawCause}
                      </pre>
                    </div>
                  </div>
                )}
              </Grid>
            )}
            {showDownload && importStatus?.signedUrl && (
              <Grid item style={{ width: '100%' }}>
                <SectionTitle>Error file</SectionTitle>
                {fileName && (
                  <FileNameContainer style={{ marginBottom: 12 }}>
                    <InsertDriveFileIcon style={{ color: '#757575', flexShrink: 0 }} />
                    <FileNameText variant="body2">{fileName}</FileNameText>
                  </FileNameContainer>
                )}
                <DownloadButton
                  variant="contained"
                  color="primary"
                  component="a"
                  href={importStatus.signedUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Download error file
                </DownloadButton>
                {importStatus.urlExpiresAt && (
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    style={{ display: 'block', marginTop: 8 }}
                  >
                    Link expires at {formatImportTimestamp(importStatus.urlExpiresAt)}
                  </Typography>
                )}
              </Grid>
            )}
            {!errorInfo.message && !hasDetails && !showDownload && (
              <Grid item>
                <Typography color="textSecondary">No additional information available.</Typography>
              </Grid>
            )}
          </Grid>
        ) : (
          <Typography color="textSecondary">
            No import data available for this collection.
          </Typography>
        )}
      </CustomModal.Content>
      <CustomModal.Footer cancelText="Close" onCancel={onClose} />
    </CustomModal>
  )
}



// WEBPACK FOOTER //
// ./src/containers/Settings/Development/MigrateCompanyProducts/ImportStatusDetailsModal.tsx